import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { PREFERENCE_KEYS, readPreference, writePreference } from '../lib/storage';

export type ThemePreference = 'system' | 'light' | 'dark';

function isTheme(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function initialTheme(): ThemePreference {
  const stored = readPreference(PREFERENCE_KEYS.theme);
  return isTheme(stored) ? stored : 'system';
}

/** [data-theme] on <html> selects the token set; without it the system preference applies. */
export function applyTheme(theme: ThemePreference): void {
  if (theme === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
}

interface ThemeValue {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(initialTheme);
  useLayoutEffect(() => applyTheme(theme), [theme]);
  const setTheme = useCallback((next: ThemePreference) => {
    writePreference(PREFERENCE_KEYS.theme, next);
    setThemeState(next);
  }, []);
  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme() must be used inside <ThemeProvider>');
  return value;
}
